import { useEffect, useState } from 'react';

import { toast } from 'sonner';

import {
  CreateRelativityListError,
  CreateRelativityListErrorCode,
} from '../../../../../../backend/src/integrations/relativity/relativity-errors';
import { TrackingEventsV2 } from '@/analytics/eventsV2';
import { trackCustomEvent } from '@/analytics/Mixpanel';
import useCreateRelativityList from '@/api/mutations/useCreateRelativityList';
import useGetConfiguredRelativityWorkspaces from '@/api/queries/useGetConfiguredRelativityWorkspaces';
import { useRelativityAuth } from '@/hooks/useRelativityAuth';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/new/components/shadcn/dialog';
import { Input } from '@/new/components/shadcn/input';
import { Label } from '@/new/components/shadcn/label';

interface CreateRelativityListDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workspaceId: number;
  matterId: string;
  docIds: string[];
  onCreated?: () => void;
}

function defaultListName(): string {
  const now = new Date();
  const yyyy = now.getFullYear();
  const mm = String(now.getMonth() + 1).padStart(2, '0');
  const dd = String(now.getDate()).padStart(2, '0');
  return `Wexler list - ${yyyy}-${mm}-${dd}`;
}

export function CreateRelativityListDialog({
  open,
  onOpenChange,
  workspaceId,
  matterId,
  docIds,
  onCreated,
}: CreateRelativityListDialogProps) {
  const [listName, setListName] = useState(defaultListName());
  const [error, setError] = useState<CreateRelativityListError | null>(null);

  const { mutate: createList, isLoading } = useCreateRelativityList();
  const { isAuthenticated, isLoading: isAuthLoading, isConnecting, connectRelativity } = useRelativityAuth();
  const { data: workspaces } = useGetConfiguredRelativityWorkspaces({ enabled: open && isAuthenticated });
  const workspaceName = workspaces?.find((w) => w.workspaceId === workspaceId)?.workspaceName ?? null;

  useEffect(() => {
    if (open) {
      setListName(defaultListName());
      setError(null);
    }
  }, [open]);

  const trimmedName = listName.trim();
  const canSubmit = trimmedName !== '' && docIds.length > 0 && !isLoading;

  const handleSubmit = () => {
    setError(null);
    createList(
      { workspaceId, matterId, listName: trimmedName, docIds },
      {
        onSuccess: ({ artifactId, skippedDuplicates }) => {
          trackCustomEvent(TrackingEventsV2.DocumentRelativityListCreated, {
            workspaceId,
            matterId,
            listArtifactId: artifactId,
            documentCount: docIds.length - skippedDuplicates,
          });
          toast.success(
            `Created list "${trimmedName}" in Relativity`,
            skippedDuplicates > 0
              ? {
                  description: `${skippedDuplicates} ${
                    skippedDuplicates === 1 ? 'document was' : 'documents were'
                  } skipped due to having the same Relativity Identifier as other documents in the selection.`,
                }
              : undefined,
          );
          onCreated?.();
          onOpenChange(false);
        },
        onError: (err) => {
          setError(err);
        },
      },
    );
  };

  const showAuthGate = !isAuthLoading && !isAuthenticated;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <Icon className="lucide--list-plus" />
            Create a List in Relativity
          </DialogTitle>
          <DialogDescription>
            {showAuthGate ? (
              <p>Sign in to Relativity to create a list from these documents.</p>
            ) : (
              <p>
                <strong>{docIds.length}</strong> {docIds.length === 1 ? 'document' : 'documents'} will be added to a new
                list in Relativity workspace <strong>{workspaceName ?? `#${workspaceId}`}</strong>.
              </p>
            )}
          </DialogDescription>
        </DialogHeader>

        {showAuthGate ? (
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="ghost" disabled={isConnecting}>
                Cancel
              </Button>
            </DialogClose>
            <Button onClick={connectRelativity} disabled={isConnecting}>
              <Icon className="lucide--log-in" />
              {isConnecting ? 'Connecting…' : 'Connect to Relativity'}
            </Button>
          </DialogFooter>
        ) : (
          <>
            <Label htmlFor="relativity-list-name">List name</Label>
            <Input
              id="relativity-list-name"
              value={listName}
              onChange={(e) => setListName(e.target.value)}
              placeholder="List name"
              maxLength={255}
              disabled={isLoading || isAuthLoading}
              autoFocus
            />

            {error && (
              <div className="my-4">
                <CreateListErrorBanner error={error} />
              </div>
            )}

            <DialogFooter>
              <DialogClose asChild>
                <Button variant="ghost" disabled={isLoading}>
                  Cancel
                </Button>
              </DialogClose>
              <Button onClick={handleSubmit} disabled={!canSubmit || isAuthLoading}>
                {isLoading ? (
                  <Icon className="lucide--loader animate-spin ease-in-out" />
                ) : (
                  <Icon className="lucide--list-plus" />
                )}
                Create List
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateListErrorBanner({ error }: { error: CreateRelativityListError }) {
  const code = error.body.code;

  if (code === CreateRelativityListErrorCode.MissingRelativityArtifacts) {
    const docs = error.body.missingDocs ?? [];

    return (
      <ErrorBox title="Some documents aren't linked to Relativity">
        <p className="mb-2">{error.body.message}</p>

        {docs.length > 0 && (
          <div className="mb-1 max-h-36 divide-y divide-gray-100 overflow-y-auto rounded-md border bg-white">
            {docs.map((d, i) => (
              <p key={d.id} className="px-2.5 py-1.5 text-gray-600">
                <span className="text-gray-400 tabular-nums">{i + 1}.</span> {d.name}
              </p>
            ))}
          </div>
        )}
      </ErrorBox>
    );
  }

  if (code === CreateRelativityListErrorCode.DocsNotFound) {
    return (
      <ErrorBox title="Selection out of date">
        <p>{error.body.message}</p>
      </ErrorBox>
    );
  }

  if (code === CreateRelativityListErrorCode.RelativityPermissionDenied) {
    return (
      <ErrorBox title="Permission denied">
        <p>{error.body.message}</p>
        {error.body.relativityMessage && <p className="mt-2">Relativity said: {error.body.relativityMessage}</p>}
      </ErrorBox>
    );
  }

  if (
    code === CreateRelativityListErrorCode.RelativityNotFound ||
    code === CreateRelativityListErrorCode.RelativityBadRequest ||
    code === CreateRelativityListErrorCode.RelativityUnavailable ||
    code === CreateRelativityListErrorCode.RelativityUnexpectedResponse
  ) {
    return (
      <ErrorBox title="Couldn't create the list">
        <p>{error.body.message}</p>
        {error.body.relativityMessage && <p className="mt-2">Relativity said: {error.body.relativityMessage}</p>}
      </ErrorBox>
    );
  }

  return (
    <ErrorBox title="Something went wrong">
      <p>{error.body.message || 'Please try again.'}</p>
      {error.status > 0 && <p className="mt-1">Status {error.status}</p>}
    </ErrorBox>
  );
}

function ErrorBox({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="rounded-lg border border-red-300 bg-red-50/50 px-3.5 py-2.5 text-sm text-red-500">
      <div className="flex items-center gap-2">
        <Icon className="lucide--alert-circle" />
        <h3 className="text-sm font-medium">{title}</h3>
      </div>

      <div className="mt-1">{children}</div>
    </div>
  );
}
